// src/pages/BakeryInformation/index.tsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import BreadCrumb from "Common/BreadCrumb";
import TableContainer from "Common/TableContainer";
import PaginationTest from "Common/Components/Paginationtest";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import {
    MapPin,
    Home,
    Calendar,
    User,
    Building2,
    Navigation,
    RefreshCw,
    Eye,
    Search,
    X,
    Pencil,
    Save,
    Loader2,
} from "lucide-react";
import Swal from "sweetalert2";
import Select from "react-select";
import axios from "axios";
import LocationModal, { hasValidLocation } from "Common/Components/LocationModal";
import LocationPickerMap from "Common/Components/LocationPickerMap";
import { getRoleId } from "Common/Components/getRoleId";
import { API_BASE_URL, getAuthHeaders } from "./api";
import { useAssistantFilter } from "Common/Assistant/lib/pageFilter";

// ========== تعریف استایل‌های Select ==========
const selectStyles = {
    control: (provided: any, state: any) => ({
        ...provided,
        borderRadius: "0.75rem",
        borderColor: state.isFocused ? "#C0151B" : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(192, 21, 27, 0.2)" : "none",
        backgroundColor: "var(--tw-bg-white, #ffffff)",
        minHeight: "42px",
        "&:hover": {
            borderColor: "#C0151B",
        },
    }),
    option: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? "#C0151B"
            : state.isFocused
                ? "rgba(192, 21, 27, 0.1)"
                : "transparent",
        color: state.isSelected ? "#ffffff" : "#1e293b",
        "&:active": {
            backgroundColor: "#C0151B",
            color: "#ffffff",
        },
    }),
    menu: (provided: any) => ({
        ...provided,
        borderRadius: "0.75rem",
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        zIndex: 9999,
    }),
    menuPortal: (provided: any) => ({
        ...provided,
        zIndex: 9999,
    }),
    placeholder: (provided: any) => ({
        ...provided,
        color: "#94a3b8",
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: "#1e293b",
    }),
};

// ========== Types مطابق با مستندات API ==========
interface Bakery {
    created_at: string;
    created_at_jalali: string;
    updated_at: string;
    updated_at_jalali: string;
    id: number;
    baker_name: string;
    baker_code: string;
    province: string;
    province_id: number;
    city: string;
    city_id: number;
    day_off: string | null;
    bakery_type: "rural" | "urban";
    location_lat: number | null;
    location_lng: number | null;
    address: string | null;
}

interface BakeryListResponse {
    total: number;
    size: number;
    pages: number;
    items: Bakery[];
}

// فرم ویرایش همه مقادیر را به صورت رشته نگه می‌دارد تا حالت «خالی» قابل تشخیص باشد
interface EditFormState {
    baker_name: string;
    province: string;
    province_id: string;
    city: string;
    city_id: string;
    day_off: string;
    bakery_type: "rural" | "urban";
    location_lat: string;
    location_lng: string;
    address: string;
}

const emptyEditForm: EditFormState = {
    baker_name: "",
    province: "",
    province_id: "",
    city: "",
    city_id: "",
    day_off: "",
    bakery_type: "urban",
    location_lat: "",
    location_lng: "",
    address: "",
};

interface ApiError {
    detail?: Array<{
        loc: (string | number)[];
        msg: string;
        type: string;
        input?: any;
        ctx?: any;
    }> | string;
}

// ========== Helpers ==========
const formatJalaliDateTime = (iso: string) => {
    try {
        const d = new Date(iso);
        return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        }).format(d);
    } catch {
        return iso;
    }
};

// مقدار api دقیقاً همان چیزی است که باید در PATCH ارسال شود (با فاصله معمولی، بدون نیم‌فاصله).
// اگر مقدار دیگری ارسال شود، روز تعطیل در وظایف بازرس بی‌اثر می‌ماند.
const CLOSURE_DAYS = [
    { api: "شنبه", label: "شنبه" },
    { api: "یکشنبه", label: "یکشنبه" },
    { api: "دوشنبه", label: "دوشنبه" },
    { api: "سه شنبه", label: "سه‌شنبه" },
    { api: "چهارشنبه", label: "چهارشنبه" },
    { api: "پنج شنبه", label: "پنج‌شنبه" },
    { api: "جمعه", label: "جمعه" },
];
const BAKERY_TYPES = [
    { value: "", label: "همه انواع" },
    { value: "urban", label: "شهری" },
    { value: "rural", label: "روستایی" },
];

const BAKERY_TYPE_LABELS: Record<string, string> = {
    urban: "شهری",
    rural: "روستایی",
};

// حداکثر اندازه صفحه‌ای که API پشتیبانی می‌کند
const API_PAGE_SIZE = 100;
// سقف ایمنی برای تعداد صفحاتی که پشت سر هم گرفته می‌شود
const MAX_FETCH_PAGES = 100;
// تعداد درخواست‌های همزمان هنگام دریافت صفحات
const FETCH_CONCURRENCY = 5;

// نیم‌فاصله/فاصله/کاراکترهای جهت‌نما را حذف می‌کند تا «سه‌شنبه» و «سه شنبه» یکسان دیده شوند
const normalizeText = (value?: string | null) =>
    (value ?? "").replace(/[\u200B-\u200F\uFEFF\s]/g, "").toLowerCase();

// ========== Main Component ==========
const BakeryInformation: React.FC = () => {
    // کل خبازی‌های دریافت‌شده از API (بدون فیلتر)
    const [allBakeries, setAllBakeries] = useState<Bakery[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [reload, setReload] = useState<boolean>(false);
    const [selectedBakery, setSelectedBakery] = useState<Bakery | null>(null);
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState<number>(10);
    const [currentPage, setCurrentPage] = useState<number>(1);

    // مودال نقشه
    const [locationBakery, setLocationBakery] = useState<Bakery | null>(null);
    const [showLocationModal, setShowLocationModal] = useState<boolean>(false);

    // مودال ویرایش (فقط admin/superadmin)
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [editingBakery, setEditingBakery] = useState<Bakery | null>(null);
    const [editForm, setEditForm] = useState<EditFormState>(emptyEditForm);
    const [saving, setSaving] = useState<boolean>(false);

    const userRole = getRoleId(localStorage.getItem("app_havir"));
    // طبق مستندات، ویرایش فقط برای admin و superadmin مجاز است
    const canEdit = userRole === 0 || userRole === 1;

    const [filters, setFilters] = useState({
        search: "",
        bakery_type: "",
        day_off: "",
    });

    // فیلتری که دستیار سامانه فرستاده (از گفتگو یا با صدا) را برمی‌دارد
    useAssistantFilter("bakeries", (filter) => {
        if (filter.pageSize) setPageSize(filter.pageSize);
        setFilters((prev) => ({ ...prev, search: filter.search || "" }));
        setCurrentPage(1);
    });

    // ========== فراخوانی API ==========
    // نکته: GET /monitoring/bakeries/ فقط page، size، sort_by، sort_order و search را
    // پشتیبانی می‌کند و پارامتری برای bakery_type یا day_off ندارد. بنابراین کل فهرست را
    // یک‌بار می‌گیریم و فیلتر/آمار/صفحه‌بندی را سمت کلاینت انجام می‌دهیم.
    const fetchBakeries = useCallback(async () => {
        setLoading(true);
        try {
            const requestPage = (page: number) =>
                axios.get<BakeryListResponse>(
                    `${API_BASE_URL}/monitoring/bakeries/?page=${page}&size=${API_PAGE_SIZE}`,
                    { headers: getAuthHeaders() }
                );

            const first = await requestPage(1);
            const items: Bakery[] = [...(first.data.items || [])];
            const totalPages = Math.min(first.data.pages || 1, MAX_FETCH_PAGES);

            // صفحات باقی‌مانده را دسته‌دسته و همزمان می‌گیریم
            const remaining = Array.from({ length: Math.max(totalPages - 1, 0) }, (_, i) => i + 2);
            for (let i = 0; i < remaining.length; i += FETCH_CONCURRENCY) {
                const batch = remaining.slice(i, i + FETCH_CONCURRENCY);
                const responses = await Promise.all(batch.map(requestPage));
                responses.forEach(res => items.push(...(res.data.items || [])));
            }

            setAllBakeries(items);
        } catch (error) {
            console.error("Error fetching bakeries:", error);
            let errorMessage = "مشکلی در دریافت اطلاعات خبازی‌ها رخ داد";

            if (axios.isAxiosError(error) && error.response) {
                const apiError = error.response.data as ApiError;
                if (typeof apiError.detail === "string") {
                    errorMessage = apiError.detail;
                } else if (Array.isArray(apiError.detail)) {
                    errorMessage = apiError.detail.map(d => d.msg).join("، ");
                }
            }

            setAllBakeries([]);
            Swal.fire({
                title: "خطا",
                text: errorMessage,
                icon: "error",
                confirmButtonColor: "#C0151B",
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBakeries();
    }, [fetchBakeries, reload]);

    // ========== اعمال فیلترها روی داده‌ها ==========
    const filteredBakeries = useMemo(() => {
        const search = normalizeText(filters.search);
        const dayOff = normalizeText(filters.day_off);

        return allBakeries.filter(bakery => {
            if (filters.bakery_type && bakery.bakery_type !== filters.bakery_type) {
                return false;
            }

            if (dayOff && normalizeText(bakery.day_off) !== dayOff) {
                return false;
            }

            if (search) {
                const haystack = normalizeText(
                    [
                        bakery.baker_name,
                        bakery.baker_code,
                        bakery.city,
                        bakery.province,
                        bakery.address,
                    ]
                        .filter(Boolean)
                        .join(" ")
                );
                if (!haystack.includes(search)) return false;
            }

            return true;
        });
    }, [allBakeries, filters]);

    // ========== صفحه‌بندی سمت کلاینت ==========
    const totalPages = Math.max(1, Math.ceil(filteredBakeries.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);

    const bakeries = useMemo(
        () => filteredBakeries.slice((safePage - 1) * pageSize, safePage * pageSize),
        [filteredBakeries, safePage, pageSize]
    );

    // اگر تعداد نتایج کم شد و صفحه فعلی دیگر وجود ندارد، به صفحه معتبر برگرد
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    // ========== مدیریت فیلترها ==========
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            search: "",
            bakery_type: "",
            day_off: "",
        });
        setCurrentPage(1);
    };

    // ========== تغییر صفحه ==========
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // ========== تغییر تعداد آیتم‌ها در هر صفحه ==========
    const handleSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    // ========== استخراج پیام خطای API ==========
    const extractApiError = (error: unknown, fallback: string) => {
        if (axios.isAxiosError(error) && error.response) {
            const apiError = error.response.data as ApiError;
            if (typeof apiError.detail === "string") return apiError.detail;
            if (Array.isArray(apiError.detail)) {
                return apiError.detail.map(d => d.msg).join("، ");
            }
        }
        return fallback;
    };

    // ========== مشاهده جزئیات ==========
    // ابتدا داده ردیف جدول نمایش داده می‌شود تا مودال بدون تأخیر باز شود،
    // سپس نسخه تازه از GET /monitoring/bakeries/{baker_code} جایگزین می‌شود.
    const handleViewDetails = async (bakery: Bakery) => {
        setSelectedBakery(bakery);
        setShowDetailModal(true);
        setDetailLoading(true);
        try {
            const response = await axios.get<Bakery>(
                `${API_BASE_URL}/monitoring/bakeries/${encodeURIComponent(bakery.baker_code)}`,
                { headers: getAuthHeaders() }
            );
            setSelectedBakery(response.data);
            // فهرست محلی هم با داده تازه هماهنگ می‌شود
            setAllBakeries(prev =>
                prev.map(b => (b.baker_code === response.data.baker_code ? response.data : b))
            );
        } catch (error) {
            // اگر جزئیات نیامد، همان داده فهرست نمایش داده می‌شود
            console.error("Error fetching bakery detail:", error);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowDetailModal(false);
        setTimeout(() => setSelectedBakery(null), 300);
    };

    // ========== نقشه ==========
    const openLocationModal = (bakery: Bakery) => {
        setLocationBakery(bakery);
        setShowLocationModal(true);
    };

    const closeLocationModal = () => {
        setShowLocationModal(false);
        setLocationBakery(null);
    };

    // ========== ویرایش ==========
    const openEditModal = (bakery: Bakery) => {
        setEditingBakery(bakery);
        setEditForm({
            baker_name: bakery.baker_name ?? "",
            province: bakery.province ?? "",
            province_id: bakery.province_id != null ? String(bakery.province_id) : "",
            city: bakery.city ?? "",
            city_id: bakery.city_id != null ? String(bakery.city_id) : "",
            // مقدار ذخیره‌شده ممکن است نیم‌فاصله داشته باشد؛ به مقدار دقیق API نگاشت می‌شود
            day_off:
                CLOSURE_DAYS.find(d => normalizeText(d.api) === normalizeText(bakery.day_off))?.api ?? "",
            bakery_type: bakery.bakery_type === "rural" ? "rural" : "urban",
            location_lat: bakery.location_lat != null ? String(bakery.location_lat) : "",
            location_lng: bakery.location_lng != null ? String(bakery.location_lng) : "",
            address: bakery.address ?? "",
        });
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        if (saving) return;
        setShowEditModal(false);
        setEditingBakery(null);
    };

    const handleEditChange = (key: keyof EditFormState, value: string) => {
        setEditForm(prev => ({ ...prev, [key]: value }));
    };

    // انتخاب موقعیت از روی نقشه: هر دو مقدار همیشه با هم ست می‌شوند
    const setEditLocation = (pickedLat: number, pickedLng: number) => {
        setEditForm(prev => ({
            ...prev,
            location_lat: String(pickedLat),
            location_lng: String(pickedLng),
        }));
    };

    // حذف موقعیت مکانی: طبق مستندات هر دو فیلد باید null ارسال شوند
    const clearEditLocation = () => {
        setEditForm(prev => ({ ...prev, location_lat: "", location_lng: "" }));
    };

    const handleSaveEdit = async () => {
        if (!editingBakery) return;

        const latRaw = editForm.location_lat.trim();
        const lngRaw = editForm.location_lng.trim();

        // location_lat و location_lng باید همیشه با هم ارسال شوند؛
        // ارسال یکی همراه با null خطای 422 می‌دهد.
        if ((latRaw === "") !== (lngRaw === "")) {
            Swal.fire({
                title: "موقعیت مکانی ناقص است",
                text: "عرض و طول جغرافیایی باید هر دو پر باشند یا هر دو خالی. برای حذف موقعیت، هر دو را خالی بگذارید.",
                icon: "warning",
                confirmButtonColor: "#C0151B",
            });
            return;
        }

        const lat = latRaw === "" ? null : Number(latRaw);
        const lng = lngRaw === "" ? null : Number(lngRaw);

        if ((lat !== null && Number.isNaN(lat)) || (lng !== null && Number.isNaN(lng))) {
            Swal.fire({
                title: "مقدار نامعتبر",
                text: "عرض و طول جغرافیایی باید عدد باشند.",
                icon: "warning",
                confirmButtonColor: "#C0151B",
            });
            return;
        }

        const provinceId = editForm.province_id.trim();
        const cityId = editForm.city_id.trim();

        const payload: Record<string, any> = {
            baker_name: editForm.baker_name.trim() || null,
            province: editForm.province.trim() || null,
            city: editForm.city.trim() || null,
            // خالی بودن روز تعطیل یعنی حذف آن → ارسال null
            day_off: editForm.day_off || null,
            bakery_type: editForm.bakery_type,
            address: editForm.address.trim() || null,
            // این دو همیشه با هم می‌روند: یا هر دو عدد، یا هر دو null
            location_lat: lat,
            location_lng: lng,
        };

        if (provinceId !== "" && !Number.isNaN(Number(provinceId))) {
            payload.province_id = Number(provinceId);
        }
        if (cityId !== "" && !Number.isNaN(Number(cityId))) {
            payload.city_id = Number(cityId);
        }

        setSaving(true);
        try {
            const response = await axios.patch<Bakery>(
                `${API_BASE_URL}/monitoring/bakeries/${encodeURIComponent(editingBakery.baker_code)}`,
                payload,
                { headers: getAuthHeaders() }
            );

            const updated = response.data;
            setAllBakeries(prev =>
                prev.map(b => (b.baker_code === updated.baker_code ? updated : b))
            );
            setSelectedBakery(prev =>
                prev && prev.baker_code === updated.baker_code ? updated : prev
            );

            setShowEditModal(false);
            setEditingBakery(null);
            Swal.fire({
                title: "ذخیره شد",
                text: "اطلاعات خبازی با موفقیت بروزرسانی شد.",
                icon: "success",
                confirmButtonColor: "#C0151B",
            });
        } catch (error) {
            console.error("Error updating bakery:", error);
            Swal.fire({
                title: "خطا در ذخیره",
                text: extractApiError(error, "بروزرسانی اطلاعات خبازی انجام نشد"),
                icon: "error",
                confirmButtonColor: "#C0151B",
            });
        } finally {
            setSaving(false);
        }
    };

    // ========== ستون‌های جدول ==========
    // ترتیب ستون‌ها دقیقاً مطابق خواسته است:
    // نام و نام خانوادگی، شهرستان، کد نانوا، روز تعطیل، موقعیت مکانی، نوع خبازی، آدرس
    const columns = useMemo(
        () => [
            {
                header: "نام و نام خانوادگی",
                accessorKey: "baker_name",
                cell: (cell: any) => (
                    <div className="font-medium text-slate-700 dark:text-zink-100">
                        {cell.getValue() || "—"}
                    </div>
                ),
            },
            {
                header: "شهرستان",
                accessorKey: "city",
                cell: (cell: any) => <span className="text-sm">{cell.getValue() || "—"}</span>,
            },
            {
                header: "کد نانوا",
                accessorKey: "baker_code",
                cell: (cell: any) => (
                    <span className="font-mono text-sm bg-slate-100 dark:bg-zink-600/30 px-2 py-1 rounded">
                        {cell.getValue()}
                    </span>
                ),
            },
            {
                header: "روز تعطیل",
                accessorKey: "day_off",
                cell: (cell: any) => {
                    const value = cell.getValue();
                    return value ? (
                        <span className="text-sm">{value}</span>
                    ) : (
                        <span className="text-xs text-slate-400">ثبت نشده</span>
                    );
                },
            },
            {
                header: "موقعیت مکانی",
                cell: (cell: any) => {
                    const bakery: Bakery = cell.row.original;
                    if (!hasValidLocation(bakery.location_lat, bakery.location_lng)) {
                        return <span className="text-xs text-slate-400">ثبت نشده</span>;
                    }
                    return (
                        <button
                            onClick={() => openLocationModal(bakery)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors border rounded-lg text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                            title="نمایش موقعیت روی نقشه"
                        >
                            <MapPin className="size-3.5" />
                            نمایش روی نقشه
                        </button>
                    );
                },
            },
            {
                header: "نوع خبازی",
                accessorKey: "bakery_type",
                cell: (cell: any) => {
                    const type = cell.getValue();
                    return (
                        <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${type === "urban"
                                    ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                                    : "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400"
                                }`}
                        >
                            {BAKERY_TYPE_LABELS[type] || type}
                        </span>
                    );
                },
            },
            {
                header: "آدرس",
                accessorKey: "address",
                cell: (cell: any) => {
                    const address = cell.getValue();
                    if (!address) {
                        return <span className="text-xs text-slate-400">ثبت نشده</span>;
                    }
                    return (
                        <div className="flex items-center gap-1.5 text-sm">
                            <Home className="size-3.5 text-emerald-500" />
                            <span className="truncate max-w-[140px]">{address}</span>
                        </div>
                    );
                },
            },
            {
                header: "عملیات",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const bakery: Bakery = cell.row.original;
                    return (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleViewDetails(bakery)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-white bg-custom-500 hover:bg-custom-600 rounded-lg transition-colors text-xs"
                                title="مشاهده جزئیات"
                            >
                                <Eye className="size-3.5" />
                                جزئیات
                            </button>
                            {/* ویرایش فقط برای admin و superadmin */}
                            {canEdit && (
                                <button
                                    onClick={() => openEditModal(bakery)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs transition-colors border rounded-lg text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100"
                                    title="ویرایش اطلاعات خبازی"
                                >
                                    <Pencil className="size-3.5" />
                                    ویرایش
                                </button>
                            )}
                        </div>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [canEdit]
    );

    // ========== آمارها (بر اساس نتیجه فیلترها، نه فقط صفحه جاری) ==========
    const stats = useMemo(() => ({
        total: filteredBakeries.length,
        urban: filteredBakeries.filter(b => b.bakery_type === "urban").length,
        rural: filteredBakeries.filter(b => b.bakery_type === "rural").length,
        hasDayOff: filteredBakeries.filter(b => b.day_off).length,
    }), [filteredBakeries]);

    const hasActiveFilters = Boolean(filters.search || filters.bakery_type || filters.day_off);

    // ========== گزینه‌های فیلتر ==========
    const dayOffOptions = [
        { value: "", label: "همه روزها" },
        ...CLOSURE_DAYS.map(d => ({ value: d.api, label: d.label })),
    ];

    const bakeryTypeOptions = BAKERY_TYPES;

    return (
        <React.Fragment>
            <BreadCrumb pageTitle="اطلاعات خبازی‌ها" title="اطلاعات خبازی‌ها" />

            <div className="space-y-5">
                {/* ========== کارت‌های آماری ========== */}
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                    <div className="card border-0 shadow-md rounded-2xl">
                        <div className="card-body p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-zink-400">
                                        {hasActiveFilters ? "نتیجه فیلتر" : "تعداد کل خبازی‌ها"}
                                    </p>
                                    <h4 className="text-2xl font-bold mt-1 text-slate-800 dark:text-white">
                                        {stats.total}
                                    </h4>
                                    {hasActiveFilters && (
                                        <p className="text-xs text-slate-400 dark:text-zink-400 mt-0.5">
                                            از {allBakeries.length} خبازی
                                        </p>
                                    )}
                                </div>
                                <div className="size-12 rounded-xl bg-custom-500/10 flex items-center justify-center text-custom-500">
                                    <Building2 className="size-6" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card border-0 shadow-md rounded-2xl">
                        <div className="card-body p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-zink-400">شهری</p>
                                    <h4 className="text-2xl font-bold mt-1 text-slate-800 dark:text-white">
                                        {stats.urban}
                                    </h4>
                                </div>
                                <div className="size-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                                    <Building2 className="size-6" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card border-0 shadow-md rounded-2xl">
                        <div className="card-body p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-zink-400">روستایی</p>
                                    <h4 className="text-2xl font-bold mt-1 text-slate-800 dark:text-white">
                                        {stats.rural}
                                    </h4>
                                </div>
                                <div className="size-12 rounded-xl bg-green-500/10 flex items-center justify-center text-green-500">
                                    <Home className="size-6" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card border-0 shadow-md rounded-2xl">
                        <div className="card-body p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-zink-400">دارای روز تعطیل</p>
                                    <h4 className="text-2xl font-bold mt-1 text-slate-800 dark:text-white">
                                        {stats.hasDayOff}
                                    </h4>
                                </div>
                                <div className="size-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
                                    <Calendar className="size-6" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ========== کارت اصلی جدول ========== */}
                <div className="card border-0 shadow-md rounded-2xl">
                    <div className="card-body p-5">
                        {/* ========== فیلترها ========== */}
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                            <div>
                                <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">
                                    جستجو
                                </label>
                                <div className="relative">
                                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
                                    <input
                                        type="text"
                                        className="form-input w-full pr-9 text-sm"
                                        placeholder="نام، کد نانوا، شهر..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange("search", e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">
                                    نوع خبازی
                                </label>
                                <Select
                                    options={bakeryTypeOptions}
                                    value={bakeryTypeOptions.find(o => o.value === filters.bakery_type) || null}
                                    onChange={(opt: any) => handleFilterChange("bakery_type", opt?.value || "")}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    placeholder="همه انواع"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">
                                    روز تعطیل
                                </label>
                                <Select
                                    options={dayOffOptions}
                                    value={dayOffOptions.find(o => o.value === filters.day_off) || null}
                                    onChange={(opt: any) => handleFilterChange("day_off", opt?.value || "")}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    placeholder="همه روزها"
                                />
                            </div>

                            <div className="flex items-end gap-2">
                                <button
                                    onClick={clearFilters}
                                    className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-slate-200 dark:bg-zink-600 hover:bg-slate-300 dark:hover:bg-zink-500 rounded-lg transition-colors"
                                >
                                    <X className="size-4" />
                                    بازنشانی
                                </button>
                                <button
                                    onClick={() => {
                                        setReload(!reload);
                                        Swal.fire({
                                            title: "در حال بروزرسانی...",
                                            text: "لطفاً منتظر بمانید",
                                            allowOutsideClick: false,
                                            didOpen: () => {
                                                Swal.showLoading();
                                            },
                                        });
                                        setTimeout(() => Swal.close(), 500);
                                    }}
                                    className="inline-flex items-center gap-1 px-4 py-2 text-sm text-white bg-custom-500 hover:bg-custom-600 rounded-lg transition-colors"
                                >
                                    <RefreshCw className="size-4" />
                                    بروزرسانی
                                </button>
                            </div>
                        </div>

                        {/* ========== انتخاب تعداد آیتم‌ها در هر صفحه ========== */}
                        <div className="flex items-center gap-3 mt-4">
                            <label className="text-sm text-slate-500 dark:text-zink-400">
                                تعداد در هر صفحه:
                            </label>
                            <TableDropDownSmall onSelect={handleSizeChange} />
                        </div>

                        {/* ========== جدول ========== */}
                        <div className="mt-5">
                            {loading ? (
                                <div className="flex justify-center py-12">
                                    <div className="spinner-border animate-spin inline-block w-10 h-10 border-4 rounded-full border-custom-500 border-t-transparent"></div>
                                </div>
                            ) : bakeries.length > 0 ? (
                                <>
                                    <TableContainer
                                        isPagination={false}
                                        columns={columns}
                                        data={bakeries}
                                        customPageSize={pageSize}
                                        customnumberfortable={pageSize}
                                        divclassName="-mx-5 overflow-x-auto"
                                        tableclassName="w-full whitespace-nowrap"
                                        theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
                                        thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600 text-xs"
                                        tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80 text-sm"
                                        PaginationClassName="hidden"
                                    />
                                    {/* کامپوننت صفحه‌بندی جداگانه */}
                                    <PaginationTest
                                        currentPage={safePage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </>
                            ) : (
                                <div className="text-center py-12 text-slate-400 dark:text-zink-400">
                                    <Building2 className="size-12 mx-auto mb-3 opacity-30" />
                                    <p>هیچ خبازی‌ای با فیلترهای انتخاب‌شده یافت نشد.</p>
                                    {hasActiveFilters && (
                                        <button
                                            onClick={clearFilters}
                                            className="mt-3 text-custom-500 hover:text-custom-600 text-sm font-medium"
                                        >
                                            بازنشانی فیلترها
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="mt-3 flex items-center justify-between text-sm text-slate-500 dark:text-zink-400">
                            <span>
                                تعداد کل: <strong>{allBakeries.length}</strong>
                            </span>
                            <span>
                                نمایش {bakeries.length} مورد از {filteredBakeries.length}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== مودال جزئیات خبازی ========== */}
            {showDetailModal && selectedBakery && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) handleCloseModal();
                    }}
                >
                    <div className="bg-white dark:bg-zink-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-scaleIn">
                        {/* هدر */}
                        <div className="flex items-center justify-between p-4 border-b dark:border-zink-500">
                            <h3 className="text-lg font-bold flex items-center gap-2 text-slate-800 dark:text-white">
                                <User className="size-5 text-custom-500" />
                                جزئیات خبازی
                                {detailLoading && (
                                    <span className="flex items-center gap-1 text-xs font-normal text-slate-400">
                                        <Loader2 className="size-3.5 animate-spin" />
                                        بروزرسانی...
                                    </span>
                                )}
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="p-1.5 hover:bg-slate-100 dark:hover:bg-zink-600 rounded-lg transition-colors"
                            >
                                <X className="size-5 text-slate-500 dark:text-zink-300" />
                            </button>
                        </div>

                        {/* محتوا */}
                        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]" dir="rtl">
                            <div className="space-y-4">
                                {/* اطلاعات اصلی */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-zink-700/30 rounded-xl">
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">نام و نام خانوادگی</p>
                                        <p className="text-base font-medium text-slate-800 dark:text-white">
                                            {selectedBakery.baker_name || "—"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">شهرستان</p>
                                        <p className="text-base font-medium text-slate-800 dark:text-white">
                                            {selectedBakery.city || "—"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">کد نانوا</p>
                                        <p className="text-base font-mono font-medium text-slate-800 dark:text-white">
                                            {selectedBakery.baker_code}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">روز تعطیل</p>
                                        <p className="text-base font-medium text-slate-800 dark:text-white">
                                            {selectedBakery.day_off || <span className="text-slate-400">ثبت نشده</span>}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">استان</p>
                                        <p className="text-base font-medium text-slate-800 dark:text-white">
                                            {selectedBakery.province || "—"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">نوع خبازی</p>
                                        <span
                                            className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${selectedBakery.bakery_type === "urban"
                                                    ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                                                    : "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400"
                                                }`}
                                        >
                                            {BAKERY_TYPE_LABELS[selectedBakery.bakery_type] || selectedBakery.bakery_type}
                                        </span>
                                    </div>
                                    <div className="md:col-span-2">
                                        <p className="text-xs text-slate-400 dark:text-zink-400">آدرس</p>
                                        <p className="text-base font-medium text-slate-800 dark:text-white">
                                            {selectedBakery.address || <span className="text-slate-400">ثبت نشده</span>}
                                        </p>
                                    </div>
                                </div>

                                {/* موقعیت مکانی */}
                                <div className="p-4 bg-blue-50 dark:bg-blue-500/10 rounded-xl border border-blue-200 dark:border-blue-500/20">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Navigation className="size-5 text-blue-600 dark:text-blue-400" />
                                        <h6 className="text-sm font-semibold text-slate-800 dark:text-white">
                                            موقعیت مکانی
                                        </h6>
                                    </div>
                                    {hasValidLocation(selectedBakery.location_lat, selectedBakery.location_lng) ? (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <p className="text-xs text-slate-400 dark:text-zink-400">عرض جغرافیایی</p>
                                                    <p className="text-sm font-mono text-slate-700 dark:text-zink-200">
                                                        {selectedBakery.location_lat}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-slate-400 dark:text-zink-400">طول جغرافیایی</p>
                                                    <p className="text-sm font-mono text-slate-700 dark:text-zink-200">
                                                        {selectedBakery.location_lng}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => openLocationModal(selectedBakery)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white transition-colors rounded-lg bg-emerald-600 hover:bg-emerald-700"
                                            >
                                                <MapPin className="size-3.5" />
                                                نمایش روی نقشه
                                            </button>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-400 dark:text-zink-400">
                                            موقعیت مکانی ثبت نشده است
                                        </p>
                                    )}
                                </div>

                                {/* اطلاعات زمانی */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-slate-50 dark:bg-zink-700/30 rounded-xl">
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">تاریخ ایجاد</p>
                                        <p className="text-sm text-slate-700 dark:text-zink-200">
                                            {selectedBakery.created_at_jalali || formatJalaliDateTime(selectedBakery.created_at)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">آخرین بروزرسانی</p>
                                        <p className="text-sm text-slate-700 dark:text-zink-200">
                                            {selectedBakery.updated_at_jalali || formatJalaliDateTime(selectedBakery.updated_at)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">شناسه</p>
                                        <p className="text-sm font-mono text-slate-700 dark:text-zink-200">
                                            {selectedBakery.id}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-zink-400">شناسه شهرستان</p>
                                        <p className="text-sm font-mono text-slate-700 dark:text-zink-200">
                                            {selectedBakery.city_id || "—"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* فوتر */}
                        <div className="flex justify-center p-4 border-t dark:border-zink-500">
                            <button
                                onClick={handleCloseModal}
                                className="px-6 py-2.5 bg-slate-200 dark:bg-zink-600 hover:bg-slate-300 dark:hover:bg-zink-500 rounded-lg transition-colors"
                            >
                                بستن
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== مودال نقشه ========== */}
            <LocationModal
                show={showLocationModal}
                onHide={closeLocationModal}
                lat={locationBakery?.location_lat}
                lng={locationBakery?.location_lng}
                title={`موقعیت مکانی خبازی${locationBakery?.baker_name ? ` - ${locationBakery.baker_name}` : ""}`}
                registeredAddress={locationBakery?.address}
                info={[
                    { label: "نانوا", value: locationBakery?.baker_name, icon: "user" },
                    { label: "کد نانوا", value: locationBakery?.baker_code, icon: "home" },
                    {
                        label: "استان / شهرستان",
                        value: [locationBakery?.province, locationBakery?.city].filter(Boolean).join(" / "),
                        icon: "city",
                    },
                    {
                        label: "نوع خبازی",
                        value: locationBakery ? BAKERY_TYPE_LABELS[locationBakery.bakery_type] : "",
                        icon: "home",
                    },
                    { label: "روز تعطیل", value: locationBakery?.day_off, icon: "date" },
                ]}
            />

            {/* ========== مودال ویرایش خبازی ========== */}
            {showEditModal && editingBakery && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeEditModal();
                    }}
                >
                    <div className="bg-white dark:bg-zink-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-scaleIn" dir="rtl">
                        <div className="flex items-center justify-between p-4 border-b dark:border-zink-500">
                            <h3 className="text-lg font-bold flex items-center gap-2 text-slate-800 dark:text-white">
                                <Pencil className="size-5 text-amber-500" />
                                ویرایش اطلاعات خبازی
                            </h3>
                            <button
                                onClick={closeEditModal}
                                disabled={saving}
                                className="p-1.5 hover:bg-slate-100 dark:hover:bg-zink-600 rounded-lg transition-colors disabled:opacity-50"
                            >
                                <X className="size-5 text-slate-500 dark:text-zink-300" />
                            </button>
                        </div>

                        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="md:col-span-2">
                                    <label className="block mb-1 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        کد نانوا (غیرقابل تغییر)
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full text-sm form-input bg-slate-100 dark:bg-zink-600"
                                        value={editingBakery.baker_code}
                                        disabled
                                    />
                                </div>

                                <div>
                                    <label className="block mb-1 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        نام و نام خانوادگی
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full text-sm form-input"
                                        value={editForm.baker_name}
                                        onChange={(e) => handleEditChange("baker_name", e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block mb-1 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        نوع خبازی
                                    </label>
                                    <Select
                                        options={BAKERY_TYPES.filter(t => t.value !== "")}
                                        value={BAKERY_TYPES.find(t => t.value === editForm.bakery_type) || null}
                                        onChange={(opt: any) => handleEditChange("bakery_type", opt?.value || "urban")}
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>

                                <div>
                                    <label className="block mb-1 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        استان
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full text-sm form-input"
                                        value={editForm.province}
                                        onChange={(e) => handleEditChange("province", e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block mb-1 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        شناسه استان
                                    </label>
                                    <input
                                        type="number"
                                        className="w-full text-sm form-input"
                                        value={editForm.province_id}
                                        onChange={(e) => handleEditChange("province_id", e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block mb-1 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        شهرستان
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full text-sm form-input"
                                        value={editForm.city}
                                        onChange={(e) => handleEditChange("city", e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block mb-1 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        شناسه شهرستان
                                    </label>
                                    <input
                                        type="number"
                                        className="w-full text-sm form-input"
                                        value={editForm.city_id}
                                        onChange={(e) => handleEditChange("city_id", e.target.value)}
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block mb-1 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        روز تعطیل
                                    </label>
                                    <Select
                                        options={dayOffOptions.map(o =>
                                            o.value === "" ? { value: "", label: "بدون روز تعطیل" } : o
                                        )}
                                        value={
                                            dayOffOptions.find(o => o.value === editForm.day_off) || {
                                                value: "",
                                                label: "بدون روز تعطیل",
                                            }
                                        }
                                        onChange={(opt: any) => handleEditChange("day_off", opt?.value || "")}
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                    <p className="mt-1 text-[11px] text-slate-400">
                                        خالی گذاشتن این فیلد، روز تعطیل ثبت‌شده را حذف می‌کند.
                                    </p>
                                </div>

                                <div className="md:col-span-2 p-3 border rounded-xl border-slate-200 dark:border-zink-500">
                                    <span className="block mb-2 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        موقعیت مکانی
                                    </span>
                                    {/* انتخاب موقعیت فقط از روی نقشه یا جست‌وجو انجام می‌شود؛
                                        چون هر دو مقدار همیشه با هم ست می‌شوند، حالت ناقص رخ نمی‌دهد. */}
                                    <LocationPickerMap
                                        lat={editForm.location_lat === "" ? null : Number(editForm.location_lat)}
                                        lng={editForm.location_lng === "" ? null : Number(editForm.location_lng)}
                                        onChange={setEditLocation}
                                        onClear={clearEditLocation}
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block mb-1 text-xs font-medium text-slate-500 dark:text-zink-400">
                                        آدرس
                                    </label>
                                    <textarea
                                        rows={3}
                                        className="w-full text-sm form-input"
                                        value={editForm.address}
                                        onChange={(e) => handleEditChange("address", e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 p-4 border-t dark:border-zink-500">
                            <button
                                onClick={closeEditModal}
                                disabled={saving}
                                className="px-5 py-2.5 bg-slate-200 dark:bg-zink-600 hover:bg-slate-300 dark:hover:bg-zink-500 rounded-lg transition-colors disabled:opacity-50"
                            >
                                انصراف
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                disabled={saving}
                                className="inline-flex items-center gap-1.5 px-5 py-2.5 text-white transition-colors rounded-lg bg-custom-500 hover:bg-custom-600 disabled:opacity-50"
                            >
                                {saving ? (
                                    <Loader2 className="size-4 animate-spin" />
                                ) : (
                                    <Save className="size-4" />
                                )}
                                ذخیره تغییرات
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* استایل‌های انیمیشن */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scaleIn {
                    from { transform: scale(0.95); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-scaleIn {
                    animation: scaleIn 0.25s ease-out;
                }
            `}</style>
        </React.Fragment>
    );
};

export default BakeryInformation;